import React, { useEffect, useState } from 'react';
import { api, EstimateBuilderDocDto } from '../../services/api';
import { Card, Btn, Badge, Input, C } from '../../components/UI';
import { SignaturePad } from '../../components/SignaturePad';
import { LineItemsCard, EstimateTotalsCard } from '../admin/EstimateBuilderPanel';

// ── Estimate requests assigned to this Runner ───────────────────────────────────
// Reuses LineItemsCard/EstimateTotalsCard from the admin Estimate Builder panel so pricing/
// catalog logic isn't duplicated — this view just adds the "who this was assigned to" scoping
// (GET /api/estimates/mine, not the admin-only GetAll) and the in-person signing flow.

export function ContractorEstimatesView() {
    const [estimates, setEstimates] = useState<EstimateBuilderDocDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const load = () => {
        setLoading(true); setError('');
        api.getMyAssignedEstimates().then(setEstimates).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, []);

    if (selectedId) {
        return <ContractorEstimateDetail id={selectedId} onBack={() => { setSelectedId(null); load(); }} />;
    }

    return (
        <div style={{ maxWidth: 900 }}>
            <h2 style={{ margin: '0 0 16px', fontSize: 20, fontWeight: 700 }}>Estimate Requests</h2>
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            {loading ? (
                <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>
            ) : estimates.length === 0 ? (
                <Card style={{ textAlign: 'center', padding: 40 }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Nothing assigned right now</div>
                    <div style={{ fontSize: 13, color: C.textSub }}>Estimate requests an admin dispatches to you will show up here.</div>
                </Card>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {estimates.map(e => (
                        <Card key={e.id} style={{ cursor: 'pointer' }}>
                            <div onClick={() => setSelectedId(e.id)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <strong>EB-{e.number}</strong>
                                        <Badge label={e.status === 'Requested' ? 'New request' : e.status} />
                                    </div>
                                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{e.prospect.name} · {e.jobSiteAddress}</div>
                                </div>
                                <div style={{ textAlign: 'right' }}>
                                    <div style={{ fontWeight: 700, fontSize: 15 }}>${e.totalAmount.toFixed(2)}</div>
                                    <div style={{ fontSize: 11, color: C.textMuted }}>{new Date(e.createdAt).toLocaleDateString()}</div>
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}

function ContractorEstimateDetail({ id, onBack }: { id: string; onBack: () => void }) {
    const [estimate, setEstimate] = useState<EstimateBuilderDocDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [sentLink, setSentLink] = useState<string | null>(null);
    const [signing, setSigning] = useState(false);
    const [signatureData, setSignatureData] = useState<string | null>(null);
    const [signedByName, setSignedByName] = useState('');
    const [declining, setDeclining] = useState(false);
    const [declineNote, setDeclineNote] = useState('');

    const load = () => {
        setLoading(true); setError('');
        api.getEstimateBuilderDoc(id).then(setEstimate).catch(e => setError(e.message)).finally(() => setLoading(false));
    };
    useEffect(load, [id]);

    const locked = estimate ? (estimate.status === 'Approved' || estimate.status === 'Declined') : false;

    const send = async () => {
        setBusy(true); setError('');
        try {
            const res = await api.sendEstimateBuilderDoc(id);
            setEstimate(res.estimate);
            setSentLink(res.publicLink);
        } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };

    const signNow = async () => {
        if (!signatureData) { setError('Have the customer sign above first.'); return; }
        if (!signedByName.trim()) { setError("Enter the customer's name."); return; }
        setBusy(true); setError('');
        try {
            const finish = (lat?: number, lng?: number) =>
                api.approveEstimateBuilderDoc(id, { signatureBase64Png: signatureData, lat, lng, signedByName: signedByName.trim() })
                    .then(res => { setEstimate(res); setSigning(false); })
                    .catch(e => setError(e.message))
                    .finally(() => setBusy(false));

            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    pos => finish(pos.coords.latitude, pos.coords.longitude),
                    () => finish(),
                    { timeout: 5000 }
                );
            } else {
                await finish();
            }
        } catch (e: any) { setError(e.message); setBusy(false); }
    };

    const declineNow = async () => {
        setBusy(true); setError('');
        try {
            const res = await api.declineEstimateBuilderDoc(id, declineNote.trim() || undefined);
            setEstimate(res);
            setDeclining(false);
        } catch (e: any) { setError(e.message); } finally { setBusy(false); }
    };

    if (loading) return <div style={{ fontSize: 13, color: C.textSub }}>Loading…</div>;
    if (!estimate) return <div style={{ color: '#DC2626' }}>{error || 'Estimate not found.'}</div>;

    return (
        <div style={{ maxWidth: 900 }}>
            <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: C.greenMid, cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
                ← Back to estimate requests
            </button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>EB-{estimate.number}</h2>
                        <Badge label={estimate.status === 'Requested' ? 'New request' : estimate.status} />
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>
                        {estimate.prospect.name}{estimate.prospect.email ? ` · ${estimate.prospect.email}` : ''}{estimate.prospect.phone ? ` · ${estimate.prospect.phone}` : ''}
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub }}>Job site: {estimate.jobSiteAddress}</div>
                </div>
                {!locked && !estimate.convertedJobId && (
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn size="sm" variant="outline" onClick={send} disabled={busy || estimate.lineItems.length === 0}>Send for later approval</Btn>
                        <Btn size="sm" onClick={() => setSigning(s => !s)} disabled={busy || estimate.lineItems.length === 0}>Customer signs now</Btn>
                        <Btn size="sm" variant="outline" onClick={() => setDeclining(d => !d)} disabled={busy}>Decline in person</Btn>
                    </div>
                )}
            </div>

            {estimate.convertedJobId && (
                <div style={{ padding: '10px 14px', background: C.greenBg, borderRadius: 8, marginBottom: 16, fontSize: 13, color: C.green }}>
                    ✓ Approved and converted to a job.
                </div>
            )}

            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}
            {sentLink && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: C.greenBg, borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
                    <span>Sent! Link:</span>
                    <code style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sentLink}</code>
                    <Btn size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(sentLink)}>Copy</Btn>
                    <button onClick={() => setSentLink(null)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.textMuted }}>✕</button>
                </div>
            )}

            {signing && (
                <Card style={{ marginBottom: 16, borderLeft: `4px solid ${C.greenMid}` }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Have the customer sign to approve</h3>
                    <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 10 }}>Total: ${estimate.totalAmount.toFixed(2)}</div>
                    <Input label="Customer's name" value={signedByName} onChange={setSignedByName} placeholder="Full name" />
                    <div style={{ marginTop: 10, marginBottom: 12 }}>
                        <SignaturePad onChange={setSignatureData} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <Btn variant="outline" onClick={() => setSigning(false)} disabled={busy}>Cancel</Btn>
                        <Btn onClick={signNow} disabled={busy} fullWidth>{busy ? 'Submitting…' : 'Approve & finish'}</Btn>
                    </div>
                </Card>
            )}

            {declining && (
                <Card style={{ marginBottom: 16, borderLeft: '4px solid #DC2626' }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Decline this estimate</h3>
                    <Input label="Note (optional)" value={declineNote} onChange={setDeclineNote} placeholder="Why the customer passed" />
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                        <Btn variant="outline" onClick={() => setDeclining(false)} disabled={busy}>Cancel</Btn>
                        <Btn onClick={declineNow} disabled={busy} fullWidth>{busy ? 'Submitting…' : 'Confirm decline'}</Btn>
                    </div>
                </Card>
            )}

            {(estimate.requestNotes || estimate.requestPhotoUrls.length > 0) && (
                <Card style={{ marginBottom: 16, borderLeft: '4px solid #F59E0B' }}>
                    <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>Customer's request</h3>
                    {estimate.requestNotes && <p style={{ margin: '0 0 12px', fontSize: 13, color: C.text, whiteSpace: 'pre-wrap' }}>{estimate.requestNotes}</p>}
                    {estimate.requestPhotoUrls.length > 0 && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: 8 }}>
                            {estimate.requestPhotoUrls.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                    <img src={url} alt={`Request photo ${i + 1}`} style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 8, border: `1px solid ${C.border}` }} />
                                </a>
                            ))}
                        </div>
                    )}
                </Card>
            )}

            <LineItemsCard estimate={estimate} locked={locked} onChanged={setEstimate} />
            <EstimateTotalsCard estimate={estimate} locked={locked} onChanged={setEstimate} />
        </div>
    );
}
